import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";

/* PartnerBrandBar
 * ---------------
 * Slim co-branded strip rendered just under the global header, ONLY when the
 * signed-in caregiver belongs to a partner organization (org_memberships row
 * exists). Shows: small org logo (or initial swatch) · org display name ·
 * "Memory Lane — in partnership with {Org}" tagline.
 *
 * Surfacing rules:
 *   - Caregiver-only. We hide for platform admins (they already see the org
 *     code list in /admin) and for the YC reviewer demo session (so the
 *     reviewer screenshots stay pristine).
 *   - Renders nothing while loading or when /api/org/me returns no orgs.
 */
export default function PartnerBrandBar({ user }) {
    const [brand, setBrand] = useState(null);

    useEffect(() => {
        // Only logged-in non-admin caregivers should fetch.
        if (!user || user === false) return;
        if (user.role === "admin") return;
        if (user.demo_session_id === "yc-demo-session") return;

        let active = true;
        (async () => {
            try {
                const me = await api.get("/org/me");
                if (!active) return;
                const orgs = me.data?.orgs || [];
                if (!orgs.length) return;
                // First org wins. (Caregivers belonging to >1 org is rare; if
                // it ever matters we can switch this to a dropdown.)
                const primary = orgs[0];
                if (primary?.code) {
                    const r = await api.get(`/org/brand/${primary.code}`);
                    if (active) setBrand({ ...r.data, code: primary.code, org_id: primary.id });
                } else {
                    if (active) setBrand({
                        name: primary?.name,
                        brand: { display_name: primary?.name },
                    });
                }
            } catch { /* fail soft — no bar */ }
        })();
        return () => { active = false; };
    }, [user]);

    if (!brand) return null;
    const b = brand.brand || {};
    const displayName = b.display_name || brand.name;
    const tagline = b.tagline;
    const primary = b.primary_color || "#1D8A85";
    const initial = (displayName || "?").trim().charAt(0).toUpperCase();

    return (
        <div
            data-testid="partner-brand-bar"
            className="w-full border-b border-[#E2E8F0] bg-gradient-to-r from-teal-soft/30 via-cream to-yellow-light/30 no-print"
            style={b.accent_color ? { backgroundImage: `linear-gradient(90deg, ${b.accent_color}22, transparent 60%)` } : undefined}
        >
            <div className="max-w-6xl mx-auto px-4 sm:px-6 py-2 flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-2.5 min-w-0">
                    {b.logo_url ? (
                        <img
                            src={b.logo_url}
                            alt={displayName}
                            data-testid="partner-brand-logo"
                            className="w-7 h-7 rounded-lg object-cover border border-cream-dark bg-white shrink-0"
                        />
                    ) : (
                        <span
                            aria-hidden="true"
                            data-testid="partner-brand-initial"
                            className="w-7 h-7 rounded-lg flex items-center justify-center text-white text-sm font-semibold shrink-0"
                            style={{ backgroundColor: primary }}
                        >
                            {initial}
                        </span>
                    )}
                    <div className="leading-tight min-w-0">
                        <p className="text-xs text-[#6E7A74]">
                            <span className="font-medium text-teal-dark">Memory Lane</span>
                            <span className="text-[#6E7A74]"> · in partnership with </span>
                        </p>
                        <p className="text-sm font-medium text-[#1F2624] truncate" data-testid="partner-brand-name">
                            {displayName}
                        </p>
                    </div>
                </div>
                {tagline && (
                    <p className="text-xs italic text-[#4C5551] hidden md:block truncate max-w-md" data-testid="partner-brand-tagline">
                        “{tagline}”
                    </p>
                )}
                <p className="text-[10px] uppercase tracking-wider text-[#6E7A74] hidden sm:block" data-testid="partner-brand-builtfor">
                    Built by Memory Lane for {displayName}
                </p>
            </div>
        </div>
    );
}
